/**
 * Reusable Staff ID Input Component with Validation
 * Validates staff ID against Staff Details table before accepting.
 * Use this component across all modules that require staff ID input.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { User, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { validateStaffId, getStaffList } from '@/services/validationService';
import { toast } from 'sonner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const StaffIdInput = ({
  value,
  onChange,
  onValidated,
  placeholder = "Scan or enter Staff ID...",
  autoFocus = false,
  disabled = false,
  showAutocomplete = true,
  className = "",
  label = "Staff ID",
  required = true,
  inputRef: externalRef = null
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isValidating, setIsValidating] = useState(false);
  const [validationState, setValidationState] = useState(null); // 'valid', 'invalid', null
  const [staffInfo, setStaffInfo] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  const inputRef = useRef(null);
  const actualRef = externalRef || inputRef;
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
      if (!value) {
        setValidationState(null);
        setStaffInfo(null);
      }
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && actualRef.current) {
      actualRef.current.focus();
    }
  }, [autoFocus]);

  // Fetch suggestions for autocomplete
  const fetchSuggestions = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setIsLoadingSuggestions(true);
    try {
      const result = await getStaffList(searchTerm, null, 10);
      setSuggestions(result.staff || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, []);

  // Debounced suggestion fetch
  useEffect(() => {
    if (showAutocomplete && inputValue && inputValue.length >= 2) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(inputValue);
      }, 300);
    } else {
      setSuggestions([]);
    }
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, showAutocomplete, fetchSuggestions]);

  const handleValidate = async (staffId) => {
    const trimmedId = (staffId || inputValue).trim();
    if (!trimmedId) {
      if (required) {
        toast.error('Please enter a Staff ID');
      }
      return false;
    }

    setIsValidating(true);
    setValidationState(null);

    try {
      const result = await validateStaffId(trimmedId);
      
      if (result.valid) {
        setValidationState('valid');
        setStaffInfo({
          staffId: result.staffId,
          staffName: result.staffName,
          designation: result.designation,
          section: result.section
        });
        onChange?.(trimmedId);
        onValidated?.({
          valid: true,
          staffId: result.staffId,
          staffName: result.staffName,
          designation: result.designation,
          section: result.section
        });
        toast.success(`Staff: ${result.staffName}`);
        return true;
      } else {
        setValidationState('invalid');
        setStaffInfo(null);
        toast.error(result.message);
        onValidated?.({ valid: false, staffId: trimmedId, message: result.message });
        return false;
      }
    } catch (error) {
      setValidationState('invalid');
      setStaffInfo(null);
      toast.error('Failed to validate Staff ID');
      return false;
    } finally {
      setIsValidating(false);
      setShowSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setValidationState(null);
    setStaffInfo(null);
    
    if (showAutocomplete && newValue.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidate();
    }
  };

  const handleSuggestionSelect = (staff) => {
    // Immediately close suggestions to prevent blur interference
    setShowSuggestions(false);
    setInputValue(staff.staff_id);
    // Use setTimeout to ensure state is updated before validation
    setTimeout(() => {
      handleValidate(staff.staff_id);
    }, 50);
  };

  const handleBlur = (e) => {
    // Check if the click is within the suggestions dropdown
    // If relatedTarget is within our component, don't close suggestions
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest('.staff-suggestions-dropdown')) {
      return;
    }
    // Delay to allow click on suggestions
    setTimeout(() => {
      setShowSuggestions(false);
    }, 250);
  };

  const getInputBorderColor = () => {
    if (validationState === 'valid') return 'border-green-500 focus:ring-green-500';
    if (validationState === 'invalid') return 'border-red-500 focus:ring-red-500';
    return '';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <User className="w-4 h-4" />
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              ref={actualRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
              placeholder={placeholder}
              disabled={disabled || isValidating}
              className={`pr-10 ${getInputBorderColor()}`}
              autoComplete="off"
              data-testid="staff-id-input"
            />
            
            {/* Status Icon */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isValidating && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
              {!isValidating && validationState === 'valid' && <CheckCircle className="w-4 h-4 text-green-500" />}
              {!isValidating && validationState === 'invalid' && <XCircle className="w-4 h-4 text-red-500" />}
            </div>
          </div>
          
          <Button
            type="button"
            onClick={() => handleValidate()}
            disabled={disabled || isValidating || !inputValue.trim()}
            className="bg-[#2D5F4F] hover:bg-[#234A3D]"
            data-testid="validate-staff-btn"
          >
            {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}
          </Button>
        </div>

        {/* Autocomplete Suggestions */}
        {showAutocomplete && showSuggestions && suggestions.length > 0 && (
          <div className="staff-suggestions-dropdown absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            {isLoadingSuggestions ? (
              <div className="p-2 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Loading...
              </div>
            ) : (
              <ul>
                {suggestions.map((staff) => (
                  <li
                    key={staff.staff_id}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent blur from firing before click
                      handleSuggestionSelect(staff);
                    }}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium">{staff.staff_id}</span>
                      <span className="text-gray-500 ml-2">- {staff.staff_name}</span>
                    </div>
                    <span className="text-xs text-gray-400">{staff.designation}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Staff Info Display */}
      {validationState === 'valid' && staffInfo && (
        <div className="bg-green-50 border border-green-200 rounded-md p-2 text-sm">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-4 h-4" />
            <span className="font-medium">{staffInfo.staffName}</span>
          </div>
          <div className="text-green-600 text-xs mt-1">
            {staffInfo.designation && <span>{staffInfo.designation}</span>}
            {staffInfo.section && <span className="ml-2">• {staffInfo.section}</span>}
          </div>
        </div>
      )}

      {validationState === 'invalid' && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-sm text-red-600">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            <span>Invalid Staff ID. Please enter a valid Staff ID from the system.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffIdInput;
